import { Tooltip as TooltipPrimitive } from "radix-ui";
import * as React from "react";

import { isKeyboardFocus } from "@/lib/input-modality";
import { cn } from "@/lib/utils";

/**
 * A short label that floats next to its trigger (shadcn's, on Radix), in the
 * dashboard's theme: ink with white text, light blue-white with navy text in
 * dark mode. It shows on hover and on keyboard focus (not on a focus the
 * browser does not show, see `TooltipTrigger`), and hides on Escape;
 * the browser's own `title` tooltip is never used. One `<TooltipProvider>`
 * around the app sets the delay.
 */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

/**
 * Radix opens the tooltip on any focus without a pointer down, so also when a
 * dialog closes and hands focus back to the row action that opened it; the
 * tooltip then stays over the table. Here only a focus the keyboard brought
 * (Tab, or a dialog closed with the keyboard) opens it; hover is unchanged.
 */
function TooltipTrigger({
  onFocus,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onFocus={(event) => {
        onFocus?.(event);
        if (!isKeyboardFocus(event.currentTarget)) {
          // Radix skips its own focus handler on a prevented event.
          event.preventDefault();
        }
      }}
      {...props}
    />
  );
}

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-foreground text-background data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 animate-in fade-in-0 zoom-in-95 z-50 w-fit max-w-72 origin-(--radix-tooltip-content-transform-origin) rounded-md px-2.5 py-1.5 text-xs font-medium text-balance",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
